import { useState } from "react";
import { Link } from "react-router-dom";
import { Slider } from "@/components/ui/slider";
import { ArrowRight, Zap, Sun, Clock, AlertTriangle } from "lucide-react";

const PRESETS = [
  { label: "Phone & Laptop", wh: 200, sun: 5 },
  { label: "RV Refrigerator", wh: 1200, sun: 5 },
  { label: "Camping Setup", wh: 500, sun: 5 },
  { label: "Solar Generator Recharge", wh: 1000, sun: 4 },
  { label: "CPAP Camping", wh: 400, sun: 5 },
  { label: "Starlink RV", wh: 900, sun: 5 },
  { label: "Weekend Cabin", wh: 2000, sun: 5 },
  { label: "Off-Grid Shed", wh: 3000, sun: 4 },
  { label: "Emergency Backup", wh: 1500, sun: 4 },
];

export default function SolarCalculator({ compact = false }) {
  const [dailyWh, setDailyWh] = useState(1000);
  const [sunHours, setSunHours] = useState(5);
  const [rechargeTime, setRechargeTime] = useState(1);
  const [efficiencyLoss, setEfficiencyLoss] = useState(20);

  const applyPreset = (preset) => {
    setDailyWh(preset.wh);
    setSunHours(preset.sun);
  };

  const effectiveHours = sunHours * rechargeTime;
  const lossMultiplier = 1 + efficiencyLoss / 100;
  const requiredWatts = Math.ceil((dailyWh / effectiveHours) * lossMultiplier);

  const getRecommendation = () => {
    if (requiredWatts <= 100) return { panels: "1× 100W panel", setup: "Portable or foldable panel" };
    if (requiredWatts <= 200) return { panels: "1× 200W panel or 2× 100W", setup: "Portable or roof-mounted" };
    if (requiredWatts <= 400) return { panels: "2× 200W panels or 1× 400W", setup: "Roof-mounted rigid panels" };
    if (requiredWatts <= 600) return { panels: "3× 200W panels", setup: "Roof-mounted system with MPPT controller" };
    if (requiredWatts <= 1000) return { panels: "5× 200W or 3× 400W panels", setup: "Multi-panel array with MPPT controller" };
    return { panels: "Multiple 400W+ panels", setup: "Full ground or roof array with MPPT and battery bank" };
  };

  const rec = getRecommendation();
  const sliders = [
    { label: "Daily Usage", aria: "Daily energy usage in watt-hours", icon: Zap, value: dailyWh, setter: setDailyWh, min: 100, max: 5000, step: 50, unit: " Wh" },
    { label: "Sun Hours / Day", aria: "Peak sun hours per day", icon: Sun, value: sunHours, setter: setSunHours, min: 2, max: 8, step: 0.5, unit: "h" },
    { label: "Recharge Days", aria: "Number of recharge days", icon: Clock, value: rechargeTime, setter: setRechargeTime, min: 1, max: 3, step: 0.5, unit: rechargeTime > 1 ? " days" : " day" },
    { label: "Efficiency Loss", aria: "System efficiency loss percentage", icon: AlertTriangle, value: efficiencyLoss, setter: setEfficiencyLoss, min: 10, max: 40, step: 5, unit: "%" },
  ];

  return (
    <section id="calculator" className={compact ? "" : "py-16 lg:py-24"}>
      <div className={compact ? "" : "max-w-4xl mx-auto px-4"}>
        {!compact && (
          <div className="mb-8">
            <span className="text-[11px] font-mono uppercase tracking-[0.2em] text-accent-text font-semibold">Free Tool</span>
            <h2 className="font-heading font-bold text-3xl lg:text-4xl mt-1 mb-2">
              Solar Panel Sizing Calculator
            </h2>
            <p className="text-muted-foreground max-w-2xl">
              Estimate how many watts of solar you need based on your daily power consumption and conditions.
            </p>
          </div>
        )}

        <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'linear-gradient(145deg, #0f172a 0%, #1e3a5f 100%)' }}>
          {/* Subtle grid overlay */}
          <div className="absolute pointer-events-none opacity-[0.04]" style={{
            backgroundImage: `repeating-linear-gradient(0deg, #faba00, #faba00 1px, transparent 1px, transparent 48px),
                              repeating-linear-gradient(90deg, #faba00, #faba00 1px, transparent 1px, transparent 48px)`,
            inset: 0,
          }} />

          <div className="relative p-6 lg:p-8">
            {/* Presets */}
            <div className="mb-8">
              <p className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: 'rgba(250,185,0,0.6)' }}>Quick Presets</p>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => applyPreset(preset)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      dailyWh === preset.wh
                        ? "bg-accent text-slate-950 font-semibold shadow-md"
                        : "text-slate-300 hover:text-white hover:bg-white/15"
                    }`}
                    style={dailyWh !== preset.wh ? { background: 'rgba(255,255,255,0.08)' } : {}}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Sliders */}
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {sliders.map((s) => (
                <div key={s.label}>
                  <div className="flex justify-between items-center mb-3">
                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <s.icon className="w-4 h-4 text-accent" /> {s.label}
                    </label>
                    <span className="font-mono font-bold text-accent">{s.value}{s.unit}</span>
                  </div>
                  <Slider
                    value={[s.value]}
                    onValueChange={(v) => s.setter(v[0])}
                    min={s.min}
                    max={s.max}
                    step={s.step}
                    aria-label={s.aria}
                    className="[&_[role=slider]]:bg-accent [&_[role=slider]]:border-accent [&_[role=slider]]:shadow-none"
                  />
                </div>
              ))}
            </div>

            {/* Result card */}
            <div className="rounded-xl p-6" style={{ background: 'rgba(250,185,0,0.07)', border: '1px solid rgba(250,185,0,0.2)' }}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: 'rgba(148,163,184,0.6)' }}>Recommended Wattage</p>
                  <p className="font-heading font-bold text-5xl text-accent">{requiredWatts}<span className="text-2xl">W</span></p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: 'rgba(148,163,184,0.6)' }}>Suggested Setup</p>
                  <p className="font-heading font-semibold text-lg text-white">{rec.panels}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: 'rgba(148,163,184,0.6)' }}>Configuration</p>
                  <p className="text-sm" style={{ color: 'rgba(148,163,184,0.8)' }}>{rec.setup}</p>
                </div>
              </div>
              <div className="text-center">
                <Link
                  to="/best-high-efficiency-solar-panels"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-accent text-slate-950 font-heading font-bold text-sm rounded-xl hover:bg-amber-400 transition-all min-h-[52px] hover:-translate-y-0.5 hover:shadow-lg"
                >
                  Compare Recommended Solar Panels <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}